import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Typography,
  Grid,
  Avatar,
  alpha,
  useTheme,
  CircularProgress,
  Tabs as MTabs,
  Tab as MTab,
} from "@mui/material";
import {
  useGetMutualFundsBrokerSummaryQuery,
  useGetInvestmentPortfolioMutualFundsDetailsQuery,
  useGetMutualFundsInvestmentAssetAllocationQuery,
  useGetMutualFundsInvestmentTransactionsQuery,
  useGetMutualFundsTransactionDetailQuery,
} from "../../store/api/finfactor.apislice";
import { sortCategorizedFunds } from "../../store/parsers/finfactor.parser";
import ROUTES from "router/routes.constants";
import PortfolioSummaryView from "components/portfolio/PortfolioSummaryView";
import OrdersView from "components/portfolio/OrdersView";
import FinfactorOrderDetailsLayout from "../../components/FinFactorInvestment/FinfactorOrderDetailsLayout";
import NodisplayComponent from "../../components/common/NodisplayComponent";
import CategoryPanel from "../../components/FinFactorInvestment/CategoryPanel";
import BrokerHeaderBanner from "../../components/FinFactorInvestment/BrokerHeaderBanner";

const BrokerDetailContainer = () => {
  const { category, brokerId, orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(orderId ? 2 : 0);
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });

  const handleDateRangeChange = ({ startDate, endDate }) => {
    const formatDateToLocalOriginal = (dateInput) => {
      if (!dateInput) return null;
      const d = new Date(dateInput);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setDateRange({
      startDate: formatDateToLocalOriginal(startDate),
      endDate: formatDateToLocalOriginal(endDate),
    });
  };

  const { data: brokerSummary, isLoading: isSummaryLoading } =
    useGetMutualFundsBrokerSummaryQuery(brokerId, {
      skip: !brokerId,
    });

  const { data: categorizedFunds, isLoading: isFundsLoading } =
    useGetInvestmentPortfolioMutualFundsDetailsQuery(brokerId, {
      skip: !brokerId,
    });

  const { data: assetAllocationResult, isLoading: isAssetAllocationLoading } =
    useGetMutualFundsInvestmentAssetAllocationQuery(brokerId, {
      skip: !brokerId,
    });

  const { data: transactionsData, isLoading: isTransactionsLoading } =
    useGetMutualFundsInvestmentTransactionsQuery(
      {
        brokerId,
        startDate: dateRange?.startDate || undefined,
        endDate: dateRange?.endDate || undefined,
      },
      {
        skip: !brokerId,
      },
    );

  const { data: transactionDetail, isLoading: isDetailLoading } =
    useGetMutualFundsTransactionDetailQuery(orderId, {
      skip: !orderId,
    });

  const isLoading =
    isSummaryLoading ||
    isFundsLoading ||
    isAssetAllocationLoading ||
    isTransactionsLoading ||
    isDetailLoading;

  const totalFundsCount = categorizedFunds
    ? Object.values(categorizedFunds).reduce(
      (sum, cat) => sum + (cat.items?.length || 0),
      0,
    )
    : 0;

  const [selectedOrder, setSelectedOrder] = useState(null);

  const tabs = [
    { label: "Portfolio Summary", id: 0 },
    {
      label: "My Mutual Funds",
      id: 1,
    },
    { label: "Transaction", id: 2 },
  ];

  // Effect to handle orderId from URL
  React.useEffect(() => {
    if (orderId) {
      const dbTransactions = transactionsData || [];
      const order = dbTransactions.find(
        (t) => t?.id?.toString() === orderId?.toString(),
      );
      if (order) {
        setSelectedOrder(order);
        setActiveTab(2); // Ensure transaction tab is active
      }
    } else {
      setSelectedOrder(null);
    }
  }, [orderId, transactionsData]);

  const brokerInfo = {
    ...(brokerSummary || {}),
    categorizedFunds: categorizedFunds || brokerSummary?.categorizedFunds || {},
    name:
      brokerSummary?.name ||
      (brokerId
        ? brokerId === "etf"
          ? "ETF"
          : brokerId === "stocks"
            ? "STOCKS"
            : brokerId.charAt(0).toUpperCase() + brokerId.slice(1)
        : "Unknown"),
    totalValue: brokerSummary?.totalValue || "₹0",
    change: brokerSummary?.change || "₹0",
    changePercentage: brokerSummary?.changePercentage || "0%",
    isPositive: brokerSummary?.isPositive ?? true,
    purchaseCost: brokerSummary?.purchaseCost || "₹0",
    gain: brokerSummary?.gain || "₹0",
    gainPercentage: brokerSummary?.gainPercentage || "0%",
    icon: brokerSummary?.icon || "",
  };

  React.useEffect(() => {
    const resolvedBrokerName = brokerInfo?.name?.trim();
    const currentBrokerName = location.state?.brokerName;

    if (
      brokerId &&
      resolvedBrokerName &&
      resolvedBrokerName !== "Unknown" &&
      resolvedBrokerName !== currentBrokerName
    ) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {
          ...(location.state || {}),
          brokerName: resolvedBrokerName,
        },
      });
    }
  }, [brokerId, brokerInfo?.name, location.pathname, location.search, location.state, navigate]);

  const sortedCategorizedFunds = React.useMemo(() =>
    sortCategorizedFunds(brokerInfo.categorizedFunds),
    [brokerInfo.categorizedFunds]
  );

  const handleTabChange = (event, newValue) => {
    // Reset transaction filter state on tab switch so each visit starts fresh.
    setDateRange({
      startDate: null,
      endDate: null,
    });

    setActiveTab(newValue);
    if (orderId) {
      const baseUrl = ROUTES.INVESTMENT_PORTFOLIO_BROKER.replace(
        ":category",
        category,
      ).replace(":brokerId", brokerId);
      navigate(baseUrl);
    }
  };

  const handleOrderClick = (id) => {
    const typeParam = new URLSearchParams(location.search).get("type");
    const url = ROUTES.INVESTMENT_PORTFOLIO_ORDER.replace(":category", category)
      .replace(":brokerId", brokerId)
      .replace(":orderId", id);
    navigate(typeParam ? `${url}?type=${typeParam}` : url);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        if (
          brokerInfo.totalValue === "₹0" ||
          brokerInfo.totalValue === "₹0.00"
        ) {
          return (
            <NodisplayComponent
              title="No Data Found"
              subtitle="No portfolio summary available for this broker."
              containerSx={{
                mt: 3,
                minHeight: "300px",
                bgcolor: "#13172a",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.05)",
              }}
            />
          );
        }
        return (
          <Box sx={{ mt: 3 }}>
            <PortfolioSummaryView
              performanceComingSoon={true}
              reverseOrder={true}
              hideHoldings={true}
              portfolioData={{
                performanceData: brokerSummary?.performanceData
                  ? { result: { data: brokerSummary.performanceData } }
                  : { result: { data: [] } },
                assetAllocationData:
                  assetAllocationResult || { result: { byHoldingType: [], byCategory: [], byMarketCap: [], byAmc: [] } },
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            {/* Categorized Listing */}
            {sortedCategorizedFunds && sortedCategorizedFunds.length > 0 ? (
              sortedCategorizedFunds.map(([name, data]) => (
                <CategoryPanel key={name} name={name} data={data} />
              ))
            ) : (
              <NodisplayComponent
                title="No Data Found"
                subtitle="No mutual funds found for this broker."
                containerSx={{
                  minHeight: "300px",
                  bgcolor: "#13172a",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              />
            )}
          </Box>
        );
      case 2:
        if (orderId) {
          if (isDetailLoading)
            return (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: "#3b82f6" }} />
              </Box>
            );
          if (transactionDetail) {
            return (
              <Box sx={{ mt: 3 }}>
                <FinfactorOrderDetailsLayout data={transactionDetail} />
              </Box>
            );
          }
        }
        return (
          <Box sx={{ mt: 3 }}>
            <OrdersView
              mockOrdersData={transactionsData || []}
              handleOrderClick={handleOrderClick}
              showTabs={false}
              showDateRange={true}
              onDateRangeChange={handleDateRangeChange}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="lg">
        {/* Broker Header Banner */}
        {!orderId && <BrokerHeaderBanner brokerInfo={brokerInfo} />}

        {!orderId && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "rgba(255,255,255,0.1)",
              mb: 3,
            }}
          >
            <MTabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "3px 3px 0 0",
                },
                "& .MuiTab-root": {
                  color: "rgba(255,255,255,0.5)",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: { xs: "14px", md: "16px" },
                  minWidth: { xs: "auto", md: 120 },
                  px: { xs: 2, md: 3 },
                  "&.Mui-selected": {
                    color: "#3b82f6",
                  },
                },
              }}
            >
              {tabs.map((tab) => (
                <MTab key={tab.id} label={tab.label} value={tab.id} />
              ))}
            </MTabs>
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: "#3b82f6" }} />
          </Box>
        ) : (
          renderTabContent()
        )}
      </Container>
    </Box>
  );
};

export default BrokerDetailContainer;
